import { collection, onSnapshot, query } from "firebase/firestore";
import { useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { db } from "src/firebase";
import { CoachRecording } from "src/features/coach-recordings/types/coach-recording.entity";
import { getGlobalNotificationsEnabled, setCoachRecordingsHasUnreadAlert } from "src/utils/browserStorage.util";

const COACH_RECORDINGS_COLLECTION = "coach_recordings";
const ALERT_LOCK_PREFIX = "coach-recordings-alert-lock";
const ALERT_LOCK_TTL_MS = 15000;

export const CoachRecordingsNotification = () => {
  const hasInitializedRef = useRef(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const soundUnlockedRef = useRef(false);
  const speechUnlockedRef = useRef(false);
  const tabIdRef = useRef(`coach-recordings-tab-${Math.random().toString(36).slice(2)}`);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const unlockAlerts = async () => {
      if (!audioContextRef.current) {
        const AudioContextClass = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (AudioContextClass) {
          audioContextRef.current = new AudioContextClass();
        }
      }

      if (audioContextRef.current?.state === "suspended") {
        await audioContextRef.current.resume();
      }

      soundUnlockedRef.current = audioContextRef.current?.state === "running";

      if ("Notification" in window && Notification.permission === "default") {
        Notification.requestPermission().catch(() => undefined);
      }

      if ("speechSynthesis" in window) {
        window.speechSynthesis.getVoices();
        speechUnlockedRef.current = true;
      }
    };

    const handleUserInteraction = () => {
      unlockAlerts().catch(() => undefined);
    };

    window.addEventListener("pointerdown", handleUserInteraction);
    window.addEventListener("keydown", handleUserInteraction);

    return () => {
      window.removeEventListener("pointerdown", handleUserInteraction);
      window.removeEventListener("keydown", handleUserInteraction);
    };
  }, []);

  const playNewRecordSound = () => {
    const audioContext = audioContextRef.current;
    if (!audioContext || !soundUnlockedRef.current) return;

    const now = audioContext.currentTime;
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(880, now);
    oscillator.frequency.exponentialRampToValueAtTime(1174, now + 0.18);

    gainNode.gain.setValueAtTime(0.0001, now);
    gainNode.gain.exponentialRampToValueAtTime(0.08, now + 0.02);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, now + 0.35);

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.start(now);
    oscillator.stop(now + 0.36);
  };

  const speakNewRecord = () => {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) return false;
    if (!speechUnlockedRef.current) return false;

    const utterance = new SpeechSynthesisUtterance("new record");
    const voices = window.speechSynthesis.getVoices();
    const preferredVoice =
      voices.find((voice) => /en/i.test(voice.lang) && /female|zira|aria|susan|samantha|google us english/i.test(voice.name)) ||
      voices.find((voice) => /en/i.test(voice.lang)) ||
      voices[0];

    if (preferredVoice) {
      utterance.voice = preferredVoice;
      utterance.lang = preferredVoice.lang;
    } else {
      utterance.lang = "en-US";
    }

    utterance.rate = 1;
    utterance.pitch = 1;
    utterance.volume = 1;

    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
    return true;
  };

  const showBrowserNotification = (record: CoachRecording, coachLabel: string) => {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission === "default") {
      Notification.requestPermission().catch(() => undefined);
      return;
    }
    if (Notification.permission !== "granted") return;

    new Notification("New coach record", {
      body: coachLabel,
      icon: "/taaly-logo.png",
      tag: `coach-record-${record.id}`,
    });
  };

  const tryClaimNotification = (notificationId: string) => {
    if (typeof window === "undefined") return true;

    const lockKey = `${ALERT_LOCK_PREFIX}:${notificationId}`;
    const lockValue = {
      owner: tabIdRef.current,
      expiresAt: Date.now() + ALERT_LOCK_TTL_MS,
    };

    try {
      const rawExistingLock = window.localStorage.getItem(lockKey);
      if (rawExistingLock) {
        const existingLock = JSON.parse(rawExistingLock) as { owner?: string; expiresAt?: number };
        if (existingLock.expiresAt && existingLock.expiresAt > Date.now()) {
          return existingLock.owner === tabIdRef.current;
        }
      }

      window.localStorage.setItem(lockKey, JSON.stringify(lockValue));
      const rawConfirmedLock = window.localStorage.getItem(lockKey);
      if (!rawConfirmedLock) return false;

      const confirmedLock = JSON.parse(rawConfirmedLock) as { owner?: string; expiresAt?: number };
      return confirmedLock.owner === tabIdRef.current;
    } catch {
      return true;
    }
  };

  useEffect(() => {
    const coachRecordingsQuery = query(collection(db, COACH_RECORDINGS_COLLECTION));

    const unsubscribe = onSnapshot(coachRecordingsQuery, (snapshot) => {
      if (!hasInitializedRef.current) {
        hasInitializedRef.current = true;
        return;
      }

      snapshot.docChanges().forEach((change) => {
        if (change.type === "removed") {
          return;
        }

        const record = { id: change.doc.id, ...change.doc.data() } as CoachRecording;
        const isManualReviewType = record.reviewType === "MANUAL" || record.reviewType === "MANAUAL_REVIEW";
        if (record.manualReviewStatus !== "PENDING" || !isManualReviewType) {
          return;
        }

        if (!getGlobalNotificationsEnabled()) {
          return;
        }

        if (!tryClaimNotification(record.id)) {
          return;
        }

        setCoachRecordingsHasUnreadAlert(true);

        const coachLabel = record.email || record.userId || "Unknown coach";
        const didSpeak = speakNewRecord();
        if (!didSpeak) {
          playNewRecordSound();
        }
        showBrowserNotification(record, coachLabel);
        toast.success(`New coach record received: ${coachLabel}`, {
          id: `coach-record-${record.id}`,
        });
      });
    });

    return () => unsubscribe();
  }, []);

  return null;
};
